import React, { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Container from 'react-bootstrap/Container';
import Table from 'react-bootstrap/Table';
import { api } from '../../lib/api';
import FacilitatorPageHeader from './FacilitatorPageHeader';

const formatDate = (value) => {
  if (!value) return '—';
  try { return new Date(value).toLocaleString('en-GB'); } catch { return String(value); }
};

const statusBadge = (status, late) => {
  if (status === 'completed') {
    return late
      ? <Badge bg="warning" text="dark">Completed late</Badge>
      : <Badge bg="success">Completed</Badge>;
  }
  if (status === 'in_progress') return <Badge bg="info">In progress</Badge>;
  return <Badge bg="secondary">Not started</Badge>;
};

const FacilitatorAssignmentResults = ({ token, assignmentId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token || !assignmentId) return;
    setLoading(true); setError('');
    api.getExerciseAssignmentResults(assignmentId, token)
      .then((res) => setData(res))
      .catch((err) => setError(err?.message || 'Could not load results.'))
      .finally(() => setLoading(false));
  }, [token, assignmentId]);

  const completedCount = (data?.results || []).filter((r) => r.status === 'completed').length;
  const total = (data?.results || []).length;
  const averageScore = (() => {
    const scores = (data?.results || []).map((r) => r.score).filter((s) => Number.isFinite(s));
    if (!scores.length) return null;
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  })();

  return (
    <div className="student-page">
      <Container className="mt-4">
        <FacilitatorPageHeader
          title={data?.assignment?.drillTitle || 'Assignment results'}
          description={data?.assignment ? (
            <>
              {data.assignment.title ? <span>{data.assignment.title}<br /></span> : null}
              Cohort: <strong>{data.assignment.cohortName}</strong>
              {' '}· Release {formatDate(data.assignment.releaseAt)}
              {data.assignment.dueAt ? <> · Due {formatDate(data.assignment.dueAt)}</> : null}
            </>
          ) : null}
          onBack={onBack}
          backLabel="Back to assignments"
        />

        {error ? <Alert variant="danger">{error}</Alert> : null}
        {loading ? <Alert variant="info">Loading…</Alert> : null}

        {data?.assignment ? (
          <Card className="container-shadow mb-3">
            <Card.Body className="d-flex flex-wrap gap-4">
              <div>
                <div className="text-muted small">Completion</div>
                <div className="h4 mb-0">{completedCount} / {total}</div>
              </div>
              <div>
                <div className="text-muted small">Average score</div>
                <div className="h4 mb-0">{averageScore === null ? '—' : `${averageScore}%`}</div>
              </div>
            </Card.Body>
          </Card>
        ) : null}

        {data?.results ? (
          <Card className="container-shadow">
            <Table responsive hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Status</th>
                  <th>Score</th>
                  <th>Started</th>
                  <th>Completed</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((r) => (
                  <tr key={r.userId}>
                    <td>
                      <div className="fw-semibold">{r.displayName}</div>
                      <div className="small text-muted">{r.email}</div>
                    </td>
                    <td>{statusBadge(r.status, r.late)}</td>
                    <td>{Number.isFinite(r.score) ? `${r.score}%` : '—'}</td>
                    <td className="small">{formatDate(r.startedAt)}</td>
                    <td className="small">{formatDate(r.completedAt)}</td>
                  </tr>
                ))}
                {!data.results.length ? (
                  <tr><td colSpan={5} className="text-center text-muted small py-3">This cohort has no members yet.</td></tr>
                ) : null}
              </tbody>
            </Table>
          </Card>
        ) : null}
      </Container>
    </div>
  );
};

export default FacilitatorAssignmentResults;
